import { alpha } from '../utils/couleurAlpha';
/**
 * themes.js
 * Catalog of built-in SaaS themes and helper functions to apply overrides dynamically.
 */

export const DEFAULT_THEMES = [
  {
    id: 'light-default',
    name: 'Lumineux Classique',
    type: 'light',
    colors: {
      accent: 'var(--color-primary)',
      accent2: '#4338ca',
      bg: 'var(--bg)',
      surface: 'var(--surface)',
      surface2: 'var(--surface2)',
      surface3: 'var(--surface3)',
      text: 'var(--text)',
      text2: 'var(--text2)',
      text3: 'var(--text2)',
      border: 'rgba(15,23,42,0.09)'
    },
    radius: '10px',
    glassmorphism: false
  },
  {
    id: 'dark-default',
    name: 'Sombre Classique',
    type: 'dark',
    colors: {
      accent: 'var(--color-primary)',
      accent2: '#818cf8',
      bg: 'var(--bg)',
      surface: 'var(--surface)',
      surface2: 'var(--surface2)',
      surface3: 'var(--surface3)',
      text: 'var(--text)',
      text2: 'var(--text2)',
      text3: 'var(--color-text-3)',
      border: 'rgba(255,255,255,0.07)'
    },
    radius: '10px',
    glassmorphism: false
  },
  {
    id: 'nordic-frost',
    name: 'Nordic Frost',
    type: 'light',
    colors: {
      accent: '#0891b2',
      accent2: '#0d9488',
      bg: '#f0f9ff',
      surface: 'var(--surface)',
      surface2: '#e0f2fe',
      surface3: '#bae6fd',
      text: 'var(--text)',
      text2: 'var(--info)',
      text3: '#0369a1',
      border: '#bae6fd'
    },
    radius: '16px',
    glassmorphism: true
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk Neon',
    type: 'dark',
    colors: {
      accent: '#f43f5e',
      accent2: '#d946ef',
      bg: '#090514',
      surface: '#140c24',
      surface2: '#090514',
      surface3: '#2d124d',
      text: '#fdf4ff',
      text2: '#f472b6',
      text3: '#a21caf',
      border: 'rgba(244,63,94,0.2)'
    },
    radius: '4px',
    glassmorphism: true
  },
  {
    id: 'forest-emerald',
    name: 'Forêt Émeraude',
    type: 'light',
    colors: {
      accent: '#059669',
      accent2: 'var(--color-success)',
      bg: '#f0fdf4',
      surface: 'var(--surface)',
      surface2: '#dcfce7',
      surface3: '#bbf7d0',
      text: '#064e3b',
      text2: 'var(--color-success)',
      text3: '#065f46',
      border: '#bbf7d0'
    },
    radius: '12px',
    glassmorphism: false
  },
  {
    id: 'corporate-navy',
    name: 'Corporate Navy',
    type: 'dark',
    colors: {
      accent: 'var(--info)',
      accent2: '#3b82f6',
      bg: '#0b1329',
      surface: '#1c2541',
      surface2: '#0b1329',
      surface3: '#3a506b',
      text: 'var(--surface)',
      text2: 'var(--color-text-3)',
      text3: '#5bc0be',
      border: '#3a506b'
    },
    radius: '8px',
    glassmorphism: false
  },
  {
    id: 'sunset-amber',
    name: 'Coucher de Soleil',
    type: 'light',
    colors: {
      accent: '#ea580c',
      accent2: 'var(--warn)',
      bg: '#fff7ed',
      surface: 'var(--surface)',
      surface2: '#ffedd5',
      surface3: '#fed7aa',
      text: '#431407',
      text2: '#9a3412',
      text3: '#c2410c',
      border: '#fed7aa'
    },
    radius: '14px',
    glassmorphism: false
  },
  {
    id: 'dracula-vamp',
    name: 'Dracula Gothic',
    type: 'dark',
    colors: {
      accent: '#ff79c6',
      accent2: '#bd93f9',
      bg: '#1e1f29',
      surface: '#282a36',
      surface2: '#1e1f29',
      surface3: '#44475a',
      text: '#f8f8f2',
      text2: '#bd93f9',
      text3: '#6272a4',
      border: '#44475a'
    },
    radius: '8px',
    glassmorphism: true
  },
  {
    id: 'neon-purple',
    name: 'Améthyste Moderne',
    type: 'dark',
    colors: {
      accent: '#a855f7',
      accent2: '#c084fc',
      bg: '#090514',
      surface: '#120b24',
      surface2: '#1a1033',
      surface3: '#25174a',
      text: '#faf5ff',
      text2: '#d8b4fe',
      text3: 'var(--accent2)',
      border: 'rgba(168,85,247,0.15)'
    },
    radius: '12px',
    glassmorphism: true
  },
  {
    id: 'monochrome-minimal',
    name: 'Monochrome Chic',
    type: 'light',
    colors: {
      accent: '#18181b',
      accent2: '#3f3f46',
      bg: 'var(--surface2)',
      surface: 'var(--surface)',
      surface2: '#f4f4f5',
      surface3: '#e4e4e7',
      text: '#09090b',
      text2: '#27272a',
      text3: '#71717a',
      border: '#e4e4e7'
    },
    radius: '0px',
    glassmorphism: false
  }
];

export const applyTheme = (themeId, customColors = {}, customRadius = null, isGlass = false) => {
  const baseTheme = DEFAULT_THEMES.find(t => t.id === themeId) || DEFAULT_THEMES[0];
  const colors = { ...baseTheme.colors, ...customColors };
  const radius = customRadius || baseTheme.radius || '10px';
  const glassActive = isGlass !== undefined ? isGlass : baseTheme.glassmorphism;

  // Sync dark/light class on document element for standard styles
  document.documentElement.setAttribute('data-theme', baseTheme.type);

  // Generate theme variable overrides dynamically to prevent self-referential circularity on the root element
  const variables = [];
  if (colors.accent) {
    variables.push(`--accent: ${colors.accent};`);
    variables.push(`--primary: ${colors.accent};`);
    variables.push(`--primary-bg: ${alpha(colors.accent, '14')};`);
    variables.push(`--accent-bg: ${alpha(colors.accent, '14')};`);
  }
  if (colors.accent2) {
    variables.push(`--accent2: ${colors.accent2};`);
  }
  if (colors.bg && colors.bg !== 'var(--bg)' && colors.bg !== 'var(--color-bg)') {
    variables.push(`--bg: ${colors.bg};`);
    variables.push(`--background: ${colors.bg};`);
  } else {
    variables.push(`--background: var(--bg);`);
  }
  if (colors.surface && colors.surface !== 'var(--surface)' && colors.surface !== 'var(--color-surface)') {
    variables.push(`--surface: ${colors.surface};`);
  }
  if (colors.surface2 && colors.surface2 !== 'var(--surface2)') {
    variables.push(`--surface2: ${colors.surface2};`);
  }
  if (colors.surface3 && colors.surface3 !== 'var(--surface3)') {
    variables.push(`--surface3: ${colors.surface3};`);
  }
  if (colors.text && colors.text !== 'var(--text)' && colors.text !== 'var(--color-text-1)') {
    variables.push(`--text: ${colors.text};`);
  }
  if (colors.text2 && colors.text2 !== 'var(--text2)' && colors.text2 !== 'var(--color-text-2)') {
    variables.push(`--text2: ${colors.text2};`);
  }
  if (colors.text3 && colors.text3 !== 'var(--color-text-3)' && colors.text3 !== 'var(--text2)') {
    variables.push(`--text3: ${colors.text3};`);
  }
  if (colors.border && colors.border !== 'var(--border)' && colors.border !== 'var(--color-border)') {
    variables.push(`--border: ${colors.border};`);
  }
  variables.push(`--radius: ${radius};`);
  // Build the CSS rules to inject
  const styleTag = document.getElementById('dynamic-theme-overrides') || document.createElement('style');
  styleTag.id = 'dynamic-theme-overrides';

  styleTag.innerHTML = `
    :root, :root[data-theme="light"], :root[data-theme="dark"] {
      ${variables.join('\n      ')}
    }

    /* Layout tweaks for SaaS visual identity */
    .panel, .card, .kpi-card, .chart-panel, .ss, .btn, .modern-input, .topbar, .sidebar, .modal-box, .forum-card {
       border-radius: ${radius} !important;
    }

    /* Glassmorphism styling overrides */
    ${glassActive ? `
    .panel, .card, .kpi-card, .chart-panel, .ss, .forum-card, .dropdown-menu {
       backdrop-filter: blur(12px) saturate(180%) !important;
       -webkit-backdrop-filter: blur(12px) saturate(180%) !important;
       background-color: ${baseTheme.type === 'dark' ? 'rgba(30, 41, 59, 0.45)' : 'rgba(255, 255, 255, 0.65)'} !important;
       border: 1px solid ${baseTheme.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'} !important;
       box-shadow: 0 8px 32px 0 ${baseTheme.type === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(31, 38, 135, 0.07)'} !important;
    }
    ` : ''}
  `;

  document.head.appendChild(styleTag);
};
